// packages/game/src/objects/DiceThrow.ts
//
// Gesture state machine + scene-coupled controller for the physical dice
// throw. Owns two simulator bodies that mirror the visual Dice objects.
//
// All Phaser-coupled bits (pointer, tweens, depth) live here. The pure
// physics is delegated to ThrowSimulator. Visuals are accessed through
// the DiceVisual interface so this controller is unit-testable without
// a Phaser scene.

import { CHIP_SCATTER_ENABLED, DEPTH, GESTURE, PHYSICS } from './diceThrowConfig.js';
import {
  type Body,
  type Bounds,
  type ImpactEvent,
  type Simulator,
} from './throwSimulator.js';

export interface DiceVisual {
  setPhysicsTransform(x: number, y: number, angleDeg: number): void;
  pickUp(liftPx?: number): void;
  drop(): void;
  showFace(face: number): void;
  startFaceFlicker(intervalMs: number): void;
  stopFaceFlicker(): void;
}

export interface ChipSourceEntry {
  id: string;
  sprite: { x: number; y: number; setPosition: (x: number, y: number) => void; setDepth: (d: number) => void };
  /** Chip radius in scene px (chips are circular). */
  halfSize: number;
}

export interface TweenAdapter {
  tween(opts: {
    durationMs: number;
    onUpdate: (alpha: number) => void;  // alpha 0..1
    onComplete: () => void;
  }): { stop: () => void };
}

export type DiceThrowState =
  | 'idle'
  | 'grabbed'
  | 'thrown'
  | 'settling'
  | 'returning';

export type DiceThrowOptions = {
  dice1: DiceVisual;
  dice2: DiceVisual;
  /** Half the visible side length of a die in scene px. Drives the OBB
   * collider's half-side so the physics box matches the sprite exactly —
   * any smaller and the dice visibly overlap before collisions fire. */
  dieHalfSize: number;
  home: { x: number; y: number; halfSep: number };
  bounds: Bounds;
  sim: Simulator;
  tween: TweenAdapter;
  rollEnabled: () => boolean;
  setRollEnabled: (v: boolean) => void;
  onRollRequest: () => void;
  onImpact?: (e: ImpactEvent) => void;
  /**
   * If provided AND CHIP_SCATTER_ENABLED is true, returns the resting chip
   * sprites on the felt at the moment of throw. Each entry's `(sprite.x,
   * sprite.y)` becomes the captured home position. Caller may omit (no-op
   * scatter — the v1 default).
   */
  chipSource?: () => ChipSourceEntry[];
};

const DICE_BODY_IDS = ['__dice_throw_d1', '__dice_throw_d2'] as const;

export class DiceThrow {
  state: DiceThrowState = 'idle';
  private opts: DiceThrowOptions;
  private fingerX = 0;
  private fingerY = 0;
  private fingerHistory: { t: number; x: number; y: number }[] = [];
  private pendingFaces: [number, number] | null = null;
  private pendingOnSettle: (() => void) | null = null;
  private pendingResolve: (() => void) | null = null;
  private hasFiredOnSettle = false;
  private returnTweenStarted = false;
  private elapsedThrowSec = 0;
  private elapsedSettlingSec = 0;
  private grabAngle = 0;
  private scatteredChips: Array<{
    id: string;
    sprite: ChipSourceEntry['sprite'];
    homeX: number; homeY: number;
    body: Body;
    cancelled: boolean;
    startX?: number; startY?: number;
  }> = [];

  constructor(opts: DiceThrowOptions) {
    this.opts = opts;
    // Seed both dice as bodies parked at home (asleep).
    const d1 = makeDieBody(DICE_BODY_IDS[0], opts.home.x - opts.home.halfSep, opts.home.y, opts.dieHalfSize);
    const d2 = makeDieBody(DICE_BODY_IDS[1], opts.home.x + opts.home.halfSep, opts.home.y, opts.dieHalfSize);
    opts.sim.add(d1);
    opts.sim.add(d2);
  }

  fingerHistoryLength(): number { return this.fingerHistory.length; }

  onPointerDown(x: number, y: number, t: number): void {
    if (this.state !== 'idle') return;
    if (!this.opts.rollEnabled()) return;
    this.state = 'grabbed';
    // Don't toggle rollEnabled here — the bridge owns that flag. The state
    // machine's `state !== 'idle'` guard above prevents re-grab on its own,
    // and the bridge will set rollEnabled=false at RollStarted.
    this.fingerX = x;
    this.fingerY = y;
    this.fingerHistory = [{ t, x, y }];
    this.opts.dice1.pickUp(GESTURE.pickUpLiftPx);
    this.opts.dice2.pickUp(GESTURE.pickUpLiftPx);
    this.opts.dice1.startFaceFlicker(GESTURE.faceFlickerIdleMs);
    this.opts.dice2.startFaceFlicker(GESTURE.faceFlickerIdleMs);
    // Attach springs — both dice target the finger directly. They will
    // jostle each other via dice-vs-dice collisions, instead of being held
    // in a rigid pair offset by halfSep.
    this.opts.sim.setSpring(DICE_BODY_IDS[0], {
      targetX: x, targetY: y,
      k: GESTURE.springStiffness, c: GESTURE.springDamping,
    });
    this.opts.sim.setSpring(DICE_BODY_IDS[1], {
      targetX: x, targetY: y,
      k: GESTURE.springStiffness, c: GESTURE.springDamping,
    });
    // Small initial torque so the dice tumble in your grip from the moment
    // you pick them up, rather than sitting flat until you flick.
    const b1 = this.opts.sim.get(DICE_BODY_IDS[0])!;
    const b2 = this.opts.sim.get(DICE_BODY_IDS[1])!;
    b1.angVel = (Math.random() - 0.5) * 2 * GESTURE.pickupTorqueRad;
    b2.angVel = (Math.random() - 0.5) * 2 * GESTURE.pickupTorqueRad;
    // Random starting orbit phase so consecutive grabs don't all begin
    // with the dice on the same axis.
    this.grabAngle = Math.random() * Math.PI * 2;
  }

  onPointerMove(x: number, y: number, t: number): void {
    if (this.state !== 'grabbed') return;
    this.fingerX = x;
    this.fingerY = y;
    this.fingerHistory.push({ t, x, y });
    // Trim history older than the velocity window (with one extra sample).
    const cutoff = t - GESTURE.fingerVelocityWindowMs;
    while (this.fingerHistory.length > 2 && this.fingerHistory[0]!.t < cutoff) {
      this.fingerHistory.shift();
    }
    // Update spring targets — both dice track the finger directly.
    const b1 = this.opts.sim.get(DICE_BODY_IDS[0]);
    const b2 = this.opts.sim.get(DICE_BODY_IDS[1]);
    if (b1?.spring) { b1.spring.targetX = x; b1.spring.targetY = y; }
    if (b2?.spring) { b2.spring.targetX = x; b2.spring.targetY = y; }
  }

  onPointerUp(x: number, y: number, t: number): void {
    if (this.state !== 'grabbed') return;
    // Detach springs first so launch velocity = current body velocity.
    this.opts.sim.setSpring(DICE_BODY_IDS[0], undefined);
    this.opts.sim.setSpring(DICE_BODY_IDS[1], undefined);
    const b1 = this.opts.sim.get(DICE_BODY_IDS[0])!;
    const b2 = this.opts.sim.get(DICE_BODY_IDS[1])!;
    // Tap detection lives on the FINGER, not the body — a stationary finger
    // is a tap even if the spring has whipped the dice into motion.
    const start = this.fingerHistory[0];
    const fingerDragPx = start ? Math.hypot(x - start.x, y - start.y) : 0;
    const isTap = fingerDragPx < GESTURE.tapDragThresholdPx;
    if (isTap) {
      // Tap → upward lob calibrated to bounce off the top wall once and
      // settle. Replace whatever the springs imparted with a clean throw.
      const H = this.opts.bounds.bottom - this.opts.bounds.top;
      const baselineVy = -Math.sqrt(2 * 1000 * H * GESTURE.tapBaselineYFactor);
      b1.vy = baselineVy; b2.vy = baselineVy;
      b1.vx = 0; b2.vx = 0;
    } else {
      // Flick → use the body velocity the spring imparted. Add a tiny jitter
      // so the two dice diverge instead of flying in lockstep.
      const jitter = GESTURE.perDieJitterPx;
      b1.vx += (Math.random() - 0.5) * jitter;
      b1.vy += (Math.random() - 0.5) * jitter;
      b2.vx += (Math.random() - 0.5) * jitter;
      b2.vy += (Math.random() - 0.5) * jitter;
    }
    // Spin is independent of tap vs flick — every throw rotates.
    b1.angVel = (Math.random() - 0.5) * GESTURE.perDieAngVelJitterRad * 2;
    b2.angVel = (Math.random() - 0.5) * GESTURE.perDieAngVelJitterRad * 2;
    // Wake bodies — velocity has been applied so they must not remain asleep.
    b1.asleep = false; b1._settleFrames = 0;
    b2.asleep = false; b2._settleFrames = 0;
    void x; void y; void t; // pointer position not needed at release
    this.fingerHistory = [];
    this.elapsedThrowSec = 0;
    this.scatteredChips = [];
    if (CHIP_SCATTER_ENABLED && this.opts.chipSource) {
      for (const c of this.opts.chipSource()) {
        const body: Body = {
          id: `__chip_${c.id}`,
          kind: 'chip',
          x: c.sprite.x, y: c.sprite.y,
          vx: 0, vy: 0,
          angle: 0, angVel: 0,
          halfSize: c.halfSize,
          mass: PHYSICS.chipMass,
          restitution: PHYSICS.diceChipRestitution,
          damping: 0.4,
          angDamping: 1,
          asleep: false,
          _settleFrames: 0,
        };
        this.opts.sim.add(body);
        c.sprite.setDepth(DEPTH.diceThrow);
        this.scatteredChips.push({
          id: c.id, sprite: c.sprite,
          homeX: c.sprite.x, homeY: c.sprite.y,
          body, cancelled: false,
        });
      }
    }
    this.state = 'thrown';
    this.opts.onRollRequest();
  }

  onPointerCancel(): void {
    if (this.state !== 'grabbed') return;
    // Detach springs and tween dice back to home (handled in Task 14's
    // Returning state — for now flag the state and let update() drive it).
    this.opts.sim.setSpring(DICE_BODY_IDS[0], undefined);
    this.opts.sim.setSpring(DICE_BODY_IDS[1], undefined);
    const b1 = this.opts.sim.get(DICE_BODY_IDS[0])!;
    const b2 = this.opts.sim.get(DICE_BODY_IDS[1])!;
    b1.vx = 0; b1.vy = 0; b1.angVel = 0;
    b2.vx = 0; b2.vy = 0; b2.angVel = 0;
    this.state = 'returning';
    // Returning tween will re-enable roll on completion (Task 14).
  }

  throwAndSettle(d1: number, d2: number, opts: { onSettle: () => void }): Promise<void> {
    this.pendingFaces = [d1, d2];
    this.pendingOnSettle = opts.onSettle;
    // If the dice are idle (spectator mode: no user input picked them up),
    // perform an automatic throw so the visual fires. The bridge will still
    // resolve onSettle once pendingFaces are consumed by the state machine.
    if (this.state === 'idle') this.autoThrow();
    return new Promise<void>((resolve) => {
      this.pendingResolve = resolve;
    });
  }

  /**
   * Programmatic throw — used by spectator mode where no user gesture
   * triggers the dice. Mirrors what onPointerDown + onPointerUp do for a
   * tap (upward lob with random spin), but skips the finger-tracking
   * spring phase.
   */
  private autoThrow(): void {
    const b1 = this.opts.sim.get(DICE_BODY_IDS[0])!;
    const b2 = this.opts.sim.get(DICE_BODY_IDS[1])!;
    // Lift the dice visually so the throw reads as a stickman-style toss
    // rather than a teleport. Mirror the pickUp call onPointerDown uses.
    this.opts.dice1.pickUp(GESTURE.pickUpLiftPx);
    this.opts.dice2.pickUp(GESTURE.pickUpLiftPx);
    this.opts.dice1.startFaceFlicker(GESTURE.faceFlickerIdleMs);
    this.opts.dice2.startFaceFlicker(GESTURE.faceFlickerIdleMs);
    // Upward lob calibrated to bounce off the top wall once and settle —
    // matches the tap branch in onPointerUp.
    const H = this.opts.bounds.bottom - this.opts.bounds.top;
    const baselineVy = -Math.sqrt(2 * 1000 * H * GESTURE.tapBaselineYFactor);
    // Tiny horizontal jitter so the two dice don't fly in perfect lockstep.
    const jitter = GESTURE.perDieJitterPx;
    b1.vy = baselineVy + (Math.random() - 0.5) * jitter;
    b2.vy = baselineVy + (Math.random() - 0.5) * jitter;
    b1.vx = (Math.random() - 0.5) * jitter;
    b2.vx = (Math.random() - 0.5) * jitter;
    b1.angVel = (Math.random() - 0.5) * GESTURE.perDieAngVelJitterRad * 2;
    b2.angVel = (Math.random() - 0.5) * GESTURE.perDieAngVelJitterRad * 2;
    b1.asleep = false; b1._settleFrames = 0;
    b2.asleep = false; b2._settleFrames = 0;
    this.elapsedThrowSec = 0;
    this.state = 'thrown';
  }

  /** Per-frame tick. Wire to scene.update / scene.events.UPDATE. */
  update(dtSec: number): void {
    if (this.state === 'grabbed') {
      // Spring is pulling the bodies toward the finger; mirror their pose
      // to the visual dice so the player sees the lag/wobble in real time.
      const b1 = this.opts.sim.get(DICE_BODY_IDS[0])!;
      const b2 = this.opts.sim.get(DICE_BODY_IDS[1])!;
      // Two springs orbit around the finger 180° apart so the dice keep
      // chasing each other in a slow rotation — they never sit still in
      // the grip. Per-frame jitter rides on top so the rotation isn't a
      // perfect circle and the OBB collisions keep firing as they pass
      // through each other.
      this.grabAngle += GESTURE.grabOrbitRadPerSec * dtSec;
      const r = GESTURE.grabOrbitRadiusPx;
      const ox1 = Math.cos(this.grabAngle) * r;
      const oy1 = Math.sin(this.grabAngle) * r;
      const ox2 = -ox1;
      const oy2 = -oy1;
      const jx1 = (Math.random() - 0.5) * 2 * GESTURE.grabFidgetPx;
      const jy1 = (Math.random() - 0.5) * 2 * GESTURE.grabFidgetPx;
      const jx2 = (Math.random() - 0.5) * 2 * GESTURE.grabFidgetPx;
      const jy2 = (Math.random() - 0.5) * 2 * GESTURE.grabFidgetPx;
      if (b1.spring) { b1.spring.targetX = this.fingerX + ox1 + jx1; b1.spring.targetY = this.fingerY + oy1 + jy1; }
      if (b2.spring) { b2.spring.targetX = this.fingerX + ox2 + jx2; b2.spring.targetY = this.fingerY + oy2 + jy2; }
      this.opts.dice1.setPhysicsTransform(b1.x, b1.y, b1.angle * (180 / Math.PI));
      this.opts.dice2.setPhysicsTransform(b2.x, b2.y, b2.angle * (180 / Math.PI));
      return;
    }
    if (this.state === 'thrown') {
      this.elapsedThrowSec += dtSec;
      // Force damping past the hard timeout so a stuck sim still settles.
      if (this.elapsedThrowSec > PHYSICS.hardTimeoutSec) {
        const b1 = this.opts.sim.get(DICE_BODY_IDS[0])!;
        const b2 = this.opts.sim.get(DICE_BODY_IDS[1])!;
        const f = 0.85;
        b1.vx *= f; b1.vy *= f; b1.angVel *= f;
        b2.vx *= f; b2.vy *= f; b2.angVel *= f;
      }
      const b1 = this.opts.sim.get(DICE_BODY_IDS[0])!;
      const b2 = this.opts.sim.get(DICE_BODY_IDS[1])!;
      this.opts.dice1.setPhysicsTransform(b1.x, b1.y, b1.angle * (180 / Math.PI));
      this.opts.dice2.setPhysicsTransform(b2.x, b2.y, b2.angle * (180 / Math.PI));
      for (const c of this.scatteredChips) {
        if (c.cancelled) continue;
        c.sprite.setPosition(c.body.x, c.body.y);
      }
      if (b1.asleep && b2.asleep && this.pendingFaces && !this.hasFiredOnSettle) {
        this.hasFiredOnSettle = true;
        this.opts.dice1.stopFaceFlicker();
        this.opts.dice2.stopFaceFlicker();
        this.opts.dice1.showFace(this.pendingFaces[0]);
        this.opts.dice2.showFace(this.pendingFaces[1]);
        this.state = 'settling';
        this.elapsedSettlingSec = 0;
        this.pendingOnSettle?.();
      }
      return;
    }
    if (this.state === 'settling' && !this.returnTweenStarted) {
      // Hold the dice at their settled pose for a beat so the player can
      // read the result before they fly back to home.
      this.elapsedSettlingSec += dtSec;
      if (this.elapsedSettlingSec >= GESTURE.settleHoldSec) {
        this.startReturn();
      }
      return;
    }
    // Cancel path: cancel transitions directly to 'returning' without
    // going through 'settling'; we still need the tween to run.
    if (this.state === 'returning' && !this.returnTweenStarted) {
      this.startReturn();
      return;
    }
  }

  /**
   * If chipId is currently scattered, return its current screen position
   * AND mark the entry cancelled so its return tween skips it. The caller
   * (bridge `flyChipForRemoval`) takes over from this point.
   */
  scatteredChipPosition(chipId: string): { x: number; y: number } | undefined {
    const c = this.scatteredChips.find((c) => c.id === chipId);
    if (!c) return undefined;
    c.cancelled = true;
    return { x: c.body.x, y: c.body.y };
  }

  private startReturn(): void {
    this.returnTweenStarted = true;
    const b1 = this.opts.sim.get(DICE_BODY_IDS[0])!;
    const b2 = this.opts.sim.get(DICE_BODY_IDS[1])!;
    const startX1 = b1.x, startY1 = b1.y, startAng1 = b1.angle;
    const startX2 = b2.x, startY2 = b2.y, startAng2 = b2.angle;
    for (const c of this.scatteredChips) {
      c.startX = c.body.x;
      c.startY = c.body.y;
    }
    const targetX1 = this.opts.home.x - this.opts.home.halfSep;
    const targetX2 = this.opts.home.x + this.opts.home.halfSep;
    const targetY = this.opts.home.y;
    this.state = 'returning';
    this.opts.tween.tween({
      durationMs: GESTURE.returnTweenMs,
      onUpdate: (a) => {
        const e = easeOutCubic(a);
        b1.x = lerp(startX1, targetX1, e);
        b1.y = lerp(startY1, targetY, e);
        b1.angle = lerp(startAng1, 0, e);
        b2.x = lerp(startX2, targetX2, e);
        b2.y = lerp(startY2, targetY, e);
        b2.angle = lerp(startAng2, 0, e);
        // Sync visuals — the 'thrown' branch of update() is skipped while
        // state==='returning', so the visuals would otherwise freeze at the
        // settled position and snap home only when drop() runs in onComplete.
        this.opts.dice1.setPhysicsTransform(b1.x, b1.y, b1.angle * (180 / Math.PI));
        this.opts.dice2.setPhysicsTransform(b2.x, b2.y, b2.angle * (180 / Math.PI));
        for (const c of this.scatteredChips) {
          if (c.cancelled) continue;
          const cx = lerp(c.startX!, c.homeX, e);
          const cy = lerp(c.startY!, c.homeY, e);
          c.sprite.setPosition(cx, cy);
        }
      },
      onComplete: () => {
        this.opts.dice1.drop();
        this.opts.dice2.drop();
        this.opts.dice1.stopFaceFlicker();
        this.opts.dice2.stopFaceFlicker();
        this.state = 'idle';
        this.pendingFaces = null;
        this.hasFiredOnSettle = false;
        this.pendingOnSettle = null;
        this.returnTweenStarted = false;
        for (const c of this.scatteredChips) {
          this.opts.sim.remove(`__chip_${c.id}`);
          c.sprite.setDepth(0);
        }
        this.scatteredChips = [];
        const resolve = this.pendingResolve;
        this.pendingResolve = null;
        // Don't unconditionally re-enable roll here — the bridge already
        // called refreshRollGate during the settle handoff and will have
        // set rollEnabled correctly based on whether a line bet still
        // exists (e.g. seven-out removes it; the player must place a new
        // pass bet before the next roll).
        resolve?.();
      },
    });
  }
}

function easeOutCubic(t: number): number { const u = 1 - t; return 1 - u * u * u; }
function lerp(a: number, b: number, t: number): number { return a + (b - a) * t; }

function makeDieBody(id: string, x: number, y: number, halfSize: number): Body {
  return {
    id, kind: 'die',
    x, y, vx: 0, vy: 0,
    angle: 0, angVel: 0,
    halfSize,
    mass: PHYSICS.diceMass,
    restitution: PHYSICS.pairRestitution,
    damping: PHYSICS.linearDampingPerSec,
    angDamping: PHYSICS.angularDampingPerSec,
    asleep: true,
    _settleFrames: 999,
  };
}
